import { Injectable, isDevMode } from '@angular/core';

/**
 * The actual Logger instance tied to a specific context.
 * Replicates the exact prototype behavior from the legacy AngularJS code.
 */
export class LoggerInstance {
  constructor(
    private context: string, 
    private isEnabledFn: () => boolean
  ) {}

  private supplant(str: string, o: any): string {
    if (!o || !str) return str;
    return str.replace(/\{([^{}]*)\}/g, (a, b) => {
      const r = o[b];
      return typeof r === 'string' || typeof r === 'number' ? String(r) : a;
    });
  }

  private getFormattedTimestamp(date: Date): string {
    return this.supplant('{0}:{1}:{2}:{3}', [
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    ]);
  }

  private _log(originalFn: 'log' | 'info' | 'warn' | 'debug' | 'error', args: any[]) {
    // Check if the central service has logging enabled
    if (!this.isEnabledFn()) {
      return;
    }

    const now = this.getFormattedTimestamp(new Date());
    let message = '';
    let supplantData: any = [];

    // Exact replica of the legacy switch statement logic
    switch (args.length) {
      case 1:
        message = this.supplant("{0} - {1}: {2}", [now, this.context, args[0]]);
        break;
      case 3:
        supplantData = args[2];
        message = this.supplant("{0} - {1}::{2}('{3}')", [now, this.context, args[0], args[1]]);
        break;
      case 2:
        if (typeof args[1] === 'string') {
          message = this.supplant("{0} - {1}::{2}('{3}')", [now, this.context, args[0], args[1]]);
        } else {
          supplantData = args[1];
          message = this.supplant("{0} - {1}: {2}", [now, this.context, args[0]]);
        }
        break;
    }

    // Call the native console method with the finalized supplanted string
    console[originalFn](this.supplant(message, supplantData));
  }

  // Public methods replicating the legacy prototype
  log(...args: any[]) { this._log('log', args); }
  info(...args: any[]) { this._log('info', args); }
  warn(...args: any[]) { this._log('warn', args); }
  debug(...args: any[]) { this._log('debug', args); }
  error(...args: any[]) { this._log('error', args); }
}

/**
 * The core Angular Service that replaces the legacy 'Logger' Provider.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  // Defaults to true, but you could default this to isDevMode() 
  // so logs automatically turn off in production.
  private _isEnabled = true;

  // Replicates: this.enabled = function(_isEnabled) {...}
  enabled(isEnabled: boolean) {
    this._isEnabled = !!isEnabled;
  }

  // Replicates: Logger.getInstance = function(context) {...}
  getInstance(context: string): LoggerInstance {
    // Pass a getter function so instances always read the current global state
    return new LoggerInstance(context, () => this._isEnabled);
  }
}